import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { submitEstimateRequest } from '../data/mock';
import { Mail, Phone, MapPin } from 'lucide-react';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    zipCode: '',
    vehicleInfo: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await submitEstimateRequest(formData);
      if (result.success) {
        toast.success(result.message);
        setFormData({
          name: '',
          email: '',
          phone: '',
          zipCode: '',
          vehicleInfo: '',
          message: ''
        });
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="contact-form" className="bg-gray-50 py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-base text-green-600 font-semibold tracking-wide uppercase">Contact Us</h2>
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            Get Your Free Estimate
          </p>
          <p className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto">
            No obligation • Fast response • Professional service
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Form */}
          <Card className="shadow-xl border-2">
            <CardHeader>
              <CardTitle className="text-2xl">Request a Quote</CardTitle>
              <CardDescription>Fill out the form and we'll get back to you within 24 hours</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="border-2 focus:border-green-600"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className="border-2 focus:border-green-600"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(717) 984-3476"
                    className="border-2 focus:border-green-600"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="zipCode">ZIP Code *</Label>
                  <Input
                    id="zipCode"
                    name="zipCode"
                    required
                    value={formData.zipCode}
                    onChange={handleChange}
                    placeholder="17073"
                    maxLength="5"
                    pattern="[0-9]{5}"
                    className="border-2 focus:border-green-600"
                  />
                  <p className="text-sm text-gray-500">We service a 40-mile radius from Newmanstown, PA</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vehicleInfo">Vehicle Information</Label>
                  <Input
                    id="vehicleInfo"
                    name="vehicleInfo"
                    value={formData.vehicleInfo}
                    onChange={handleChange}
                    placeholder="2020 Honda Accord"
                    className="border-2 focus:border-green-600"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message / Service Interest</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about the service you're interested in..."
                    rows={4}
                    className="border-2 focus:border-green-600"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl transition-all py-6 text-lg"
                >
                  {isSubmitting ? 'Submitting...' : 'Get Free Estimate'}
                </Button>

                <p className="text-center text-sm text-gray-500">
                  No obligation • We respect your privacy
                </p>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="space-y-6">
            <Card className="shadow-xl border-2 hover:border-green-600 transition-colors">
              <CardHeader>
                <div className="flex items-center">
                  <Phone className="h-6 w-6 text-green-600 mr-3" />
                  <CardTitle>Call Us</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <a 
                  href="tel:717-984-3476"
                  className="text-2xl font-bold text-gray-900 hover:text-green-600 transition-colors"
                >
                  717-984-3476
                </a>
                <p className="text-gray-600 mt-2">Monday - Saturday</p>
                <p className="text-gray-600">8:00 AM - 8:00 PM</p>
              </CardContent>
            </Card>

            <Card className="shadow-xl border-2 hover:border-green-600 transition-colors">
              <CardHeader>
                <div className="flex items-center">
                  <Mail className="h-6 w-6 text-green-600 mr-3" />
                  <CardTitle>Email Us</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <a 
                  href="mailto:Primedetail@gmail.com"
                  className="text-xl font-bold text-gray-900 hover:text-green-600 transition-colors break-all"
                >
                  Primedetail@gmail.com
                </a>
                <p className="text-gray-600 mt-2">We respond within 24 hours</p>
              </CardContent>
            </Card>

            <Card className="shadow-xl border-2 hover:border-green-600 transition-colors">
              <CardHeader>
                <div className="flex items-center">
                  <MapPin className="h-6 w-6 text-green-600 mr-3" />
                  <CardTitle>Service Area</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-xl font-bold text-gray-900">Newmanstown, PA 17073</p>
                <p className="text-gray-600 mt-2">40-mile radius mobile service</p>
                <p className="text-green-700 font-semibold mt-3">We come to you - No shop location</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
